/**
 * 产品 API — 列表 + 详情 + 诊断 + 场景 + 素材 + 投放计划。
 */
import client from "./client";
import type {
  ProductListItem,
  ProductDetail,
  DiagnosisHistory,
  GeoScore,
  SceneAnalysis,
  SemanticAsset,
  DeliveryPlan,
  ProblemMatch,
  PaginatedData,
} from "../types";

/** 产品列表查询参数。 */
export interface ProductListParams {
  company_id?: string;
  brand_id?: string;
  page?: number;
  size?: number;
}

/**
 * 获取产品列表（分页）。
 * GET /api/products?company_id=&brand_id=&page=&size=
 */
export async function getProducts(
  params?: ProductListParams,
): Promise<PaginatedData<ProductListItem>> {
  return client.get<unknown, PaginatedData<ProductListItem>>("/products", {
    params,
  });
}

/**
 * 获取产品详情聚合页数据。
 * GET /api/products/{id}
 */
export async function getProductDetail(
  productId: string,
): Promise<ProductDetail> {
  return client.get<unknown, ProductDetail>(`/products/${productId}`);
}

/**
 * 获取产品诊断历史。
 * GET /api/products/{id}/diagnosis
 */
export async function getProductDiagnosis(
  productId: string,
): Promise<DiagnosisHistory[]> {
  return client.get<unknown, DiagnosisHistory[]>(
    `/products/${productId}/diagnosis`,
  );
}

/**
 * 获取产品 GEO 综合分六维聚合（指标字典 §13.2，批次制）。
 * GET /api/products/{id}/geo-score
 * data 为 null 表示接口无数据 → 调用方按 §13.3 门禁不渲染 GEO 综合分区块。
 */
export async function getProductGeoScore(
  productId: string,
): Promise<GeoScore | null> {
  return client.get<unknown, GeoScore | null>(`/products/${productId}/geo-score`);
}

/**
 * 获取产品最新诊断。
 * GET /api/products/{id}/diagnosis/latest
 */
export async function getLatestDiagnosis(
  productId: string,
): Promise<DiagnosisHistory | null> {
  return client.get<unknown, DiagnosisHistory | null>(
    `/products/${productId}/diagnosis/latest`,
  );
}

/**
 * 获取产品场景分析列表。
 * GET /api/products/{id}/scenes
 */
export async function getProductScenes(
  productId: string,
): Promise<SceneAnalysis[]> {
  return client.get<unknown, SceneAnalysis[]>(`/products/${productId}/scenes`);
}

/**
 * 获取产品语义素材列表。
 * GET /api/products/{id}/semantic-assets
 */
export async function getProductSemanticAssets(
  productId: string,
): Promise<SemanticAsset[]> {
  return client.get<unknown, SemanticAsset[]>(
    `/products/${productId}/semantic-assets`,
  );
}

/**
 * 获取产品关联的投放计划。
 * GET /api/products/{id}/delivery-plans
 */
export async function getProductDeliveryPlans(
  productId: string,
): Promise<DeliveryPlan[]> {
  return client.get<unknown, DeliveryPlan[]>(
    `/products/${productId}/delivery-plans`,
  );
}

/**
 * 获取产品客户问题与产品匹配列表（PRD §1.6 ③，独立表）。
 * GET /api/products/{id}/problem-matches
 */
export async function getProblemMatches(
  productId: string,
): Promise<ProblemMatch[]> {
  return client.get<unknown, ProblemMatch[]>(
    `/products/${productId}/problem-matches`,
  );
}
